import React, { useState } from "react";
import { toast } from "sonner";
import { postBmsControl, type ControlChannel } from "@client/api/bms";
import { t } from "@client/lib/dictionary";
import { useUserStore } from "@client/store/user";
import type { BmsMosfetState } from "@client/types/bms";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@client/components/ui/alert-dialog";
import { Button } from "@client/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@client/components/ui/card";

interface Props {
  slug: string;
  mosfet: BmsMosfetState | undefined;
}

interface Pending {
  channel: ControlChannel;
  on: boolean;
  label: string;
  consequence: string;
}

const channelLabel: Record<ControlChannel, string> = {
  charge: t.bms.chargeMosfet,
  discharge: t.bms.dischargeMosfet,
  balance: t.bms.balancer,
};

const consequence = (channel: ControlChannel, on: boolean): string => {
  if (channel === "charge") {
    return on
      ? "Povolíš nabíjení baterie. Připojený zdroj může začít cely nabíjet."
      : "Odpojíš nabíjení. Připojený nabíjecí zdroj nebude moci dodávat proud.";
  }
  if (channel === "discharge") {
    return on
      ? "Povolíš odběr ze zátěže. Připojená zátěž bude moci čerpat proud."
      : "Odpojíš zátěž. Spotřebiče ztratí napájení.";
  }
  return on ? "Spustíš aktivní balancer mezi články." : "Vypneš aktivní balancer (články se nebudou vyrovnávat).";
};

export const BmsControls: React.FC<Props> = ({ slug, mosfet }) => {
  const user = useUserStore((s) => s.user);
  const isAdmin = typeof user === "object" && user !== null && (user as { role: string }).role === "ADMIN";
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);

  if (!isAdmin) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.bms.controls}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">{t.bms.adminRequired}</CardContent>
      </Card>
    );
  }

  const trigger = (channel: ControlChannel, on: boolean) =>
    setPending({ channel, on, label: channelLabel[channel], consequence: consequence(channel, on) });

  const execute = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const res = await postBmsControl(slug, pending.channel, pending.on);
      if (res.success) {
        toast.success(`${pending.label} ${pending.on ? "ON" : "OFF"}`);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg ?? String(err));
    } finally {
      setBusy(false);
      setPending(null);
    }
  };

  const renderRow = (channel: ControlChannel, on: boolean) => (
    <div key={channel} className="flex items-center justify-between gap-4 py-2 border-b border-border last:border-0">
      <div>
        <div className="font-medium">{channelLabel[channel]}</div>
        <div className="text-xs text-muted-foreground">{t.status[on ? "online" : "offline"]}</div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant={on ? "outline" : "default"} disabled={!on} onClick={() => trigger(channel, false)}>
          {t.bms.off}
        </Button>
        <Button size="sm" variant={!on ? "outline" : "default"} disabled={on} onClick={() => trigger(channel, true)}>
          {t.bms.on}
        </Button>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.bms.controls}</CardTitle>
        <p className="text-xs text-amber-600">⚠ {t.bms.controlsWarning}</p>
      </CardHeader>
      <CardContent>
        {renderRow("charge", !!mosfet?.chargeOn)}
        {renderRow("discharge", !!mosfet?.dischargeOn)}
        {renderRow("balance", !!mosfet?.balancing)}
      </CardContent>
      <AlertDialog open={!!pending} onOpenChange={(o) => !o && !busy && setPending(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.bms.confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {pending && (
                <>
                  <div className="font-semibold text-foreground mb-2">
                    {pending.label}: {pending.on ? t.bms.on : t.bms.off}
                  </div>
                  <div>{pending.consequence}</div>
                  <div className="mt-2 text-xs">{t.bms.confirmDescription}</div>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t.app.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={execute} disabled={busy}>
              {busy ? t.app.loading : t.bms.confirmYes}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
